// LT-511: time-slot picker. Two-stage: tap a slot → "Continue" sibling
// reveals; tap Continue → page advances to the details phase.
import React from 'react';
import clsx from 'clsx';
import { Calendar as CalendarIcon, ChevronRight } from 'lucide-react';
import { Slot } from '../../../services/publicBookingService';
import { formatDayHeading, slotTime } from '../formatters';

interface Props {
  date: string | null;       // YYYY-MM-DD in the host tz
  slots: Slot[];             // already-filtered to this date
  timezone: string;
  selected: string | null;   // UTC ISO of the selected slot
  onSelect: (iso: string | null) => void;
  onContinue: () => void;
}

const TimeSlotList: React.FC<Props> = ({
  date, slots, timezone, selected, onSelect, onContinue,
}) => {
  if (!date) {
    return (
      <div
        className="flex flex-col items-center justify-center text-center px-6 py-10 text-slate-500 border border-dashed border-white/10 rounded-ds-xl min-h-[320px]"
        data-testid="public-booking-empty-day"
      >
        <CalendarIcon className="w-5 h-5" />
        <div className="mt-3 text-[13px]">Pick a date to see times</div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      <div className="flex items-baseline justify-between mb-3">
        <div className="text-sm font-semibold text-slate-50">
          {formatDayHeading(date)}
        </div>
        <div
          className="font-mono text-[11px] text-slate-500"
          style={{ fontVariantNumeric: 'tabular-nums' }}
        >
          {slots.length} {slots.length === 1 ? 'slot' : 'slots'}
        </div>
      </div>
      <div className="flex flex-col gap-2 overflow-y-auto pr-1 max-h-[460px] bp-fade-in">
        {slots.map((s) => {
          const isSel = selected === s.start;
          const label = slotTime(s.start, timezone);
          return (
            <div key={s.start} className="flex gap-2">
              <button
                type="button"
                onClick={() => onSelect(isSel ? null : s.start)}
                className={clsx(
                  'flex-1 px-3 py-2.5 rounded-ds-lg border text-[13px] font-medium transition-colors',
                  'focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface-0',
                  isSel
                    ? 'bg-surface-3 border-white/[0.16] text-slate-400 cursor-default'
                    : 'bg-surface-2 border-white/[0.08] text-slate-50 hover:bg-accent-tint hover:border-accent-border hover:text-accent-text',
                )}
                style={{ fontVariantNumeric: 'tabular-nums' }}
              >
                {label}
              </button>
              {isSel && (
                <button
                  type="button"
                  onClick={onContinue}
                  className="flex-1 px-3 py-2.5 rounded-ds-lg bg-accent text-white text-[13px] font-medium hover:bg-accent-hover inline-flex items-center justify-center gap-1 bp-fade-in focus:outline-none focus-visible:ring-2 focus-visible:ring-accent focus-visible:ring-offset-2 focus-visible:ring-offset-surface-0"
                >
                  Continue
                  <ChevronRight className="w-3 h-3" />
                </button>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default TimeSlotList;
